import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Archive, ArrowRight, Ban, CircleCheckBig, Landmark, Pencil, Printer, RotateCcw, Send, Trash2, Undo2, Wallet, X } from 'lucide-react';
import * as hr from '../../api';
import { hrSubUrl } from '../../catalog';
import Modal from '../../components/Modal';
import { Select } from '../../components/Controls';
import { fmtMoney } from '../../format';
import { Chip } from '../attendance/shared';
import Loader from '../../components/Loader';

// HR & People > HR Expenses: shared formats, charts, the detail modal with the approval actions, Send Message (Stephanet's
// custom/hrexpenses/shared.jsx). Approving, rejecting and returning go to whoever holds the expense's current approval step; payment
// stops at approval, so Post to Finance and Mark Paid say "not available yet".

export const naira = v => fmtMoney(v || 0);
export const kAxis = v => `₦${Math.round(v / 1000).toLocaleString()}k`;
export const mAxis = v => `₦${(v / 1e6).toFixed(1)}M`;
export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const COMMITTED = ['Approved'];
export const REVIEW = ['Submitted', 'Pending Review', 'HR Reviewed', 'Accounts Reviewed', 'Tax Reviewed'];
export const NOT_YET = 'Not available yet — payment stops at approval until the company decides how approved expenses reach Payment Vouchers.';
export const STATUS_COLOR = {
  Approved: '#16a34a', 'Pending Review': '#f59e0b', Rejected: '#dc2626', Draft: '#94a3b8', 'Returned for Correction': '#ea580c', Submitted: '#0284c7',
  'HR Reviewed': '#a855f7', 'Accounts Reviewed': '#7c3aed', 'Tax Reviewed': '#db2777', Cancelled: '#64748b', Archived: '#475569',
};
export const STATUS_TONE = {
  Approved: 'green', 'Pending Review': 'yellow', Rejected: 'red', Draft: 'gray', 'Returned for Correction': 'orange', Submitted: 'blue',
  'HR Reviewed': 'purple', 'Accounts Reviewed': 'purple', 'Tax Reviewed': 'purple', Cancelled: 'gray', Archived: 'gray',
};
export const POSTING_TONE = { 'Not available yet': 'yellow', 'Not Posted': 'gray' };
export const PALETTE = ['#2563eb', '#16a34a', '#f59e0b', '#dc2626', '#7c3aed', '#0d9488', '#ea580c', '#db2777', '#0284c7', '#65a30d'];
export const Status = ({ s }) => <Chip tone={STATUS_TONE[s]}>{s}</Chip>;
export const sum = (rows, k = 'amount') => rows.reduce((t, r) => t + (Number(r[k]) || 0), 0);
export const active = rows => rows.filter(r => !['Cancelled', 'Archived'].includes(r.status));
export const go = (key, params) => hrSubUrl(key, params);

export function groupBy(rows, key, top) {
  const m = new Map();
  rows.forEach(r => { const k = r[key] || '—'; const x = m.get(k) || { name: k, value: 0, count: 0 }; x.value += r.amount; x.count += 1; m.set(k, x); });
  const out = [...m.values()].sort((a, b) => b.value - a.value);
  return top ? out.slice(0, top) : out;
}
export function statusCounts(rows) {
  const m = new Map();
  rows.forEach(r => m.set(r.status, (m.get(r.status) || 0) + 1));
  return Object.keys(STATUS_COLOR).filter(s => m.has(s)).map(s => ({ name: s, value: m.get(s), color: STATUS_COLOR[s] }));
}
// 12 months of the year (or the last 12 months when no year is chosen)
export function monthlyTrend(rows, year) {
  const now = new Date();
  const keys = year ? MONTHS.map((_, i) => `${year}-${i + 1}`)
    : Array.from({ length: 12 }, (_, i) => { const d = new Date(now.getFullYear(), now.getMonth() - 11 + i, 1); return `${d.getFullYear()}-${d.getMonth() + 1}`; });
  const m = Object.fromEntries(keys.map(k => [k, 0]));
  rows.forEach(r => { const k = `${r.year}-${r.month}`; if (k in m) m[k] += r.amount; });
  return keys.map(k => { const [y, mo] = k.split('-'); return { name: year ? MONTHS[mo - 1] : `${MONTHS[mo - 1]} ${String(y).slice(2)}`, value: m[k] }; });
}

let metaCache = null;
export function useMeta() {
  const [meta, setMeta] = useState(metaCache);
  const reload = useCallback(() => hr.hreMeta().then(m => { metaCache = m; setMeta(m); return m; }), []);
  useEffect(() => { reload().catch(() => {}); }, [reload]);
  return [meta, reload];
}
export const clearMeta = () => { metaCache = null; };

export function Header({ title = 'HR Expenses', sub = 'Record, approve, and report all HR-related staff expenses and vouchers', children }) {
  return (
    <div className="mdr-top hre-top">
      <div><h2>{title}</h2><p>{sub}</p></div>
      <div className="hre-top-actions">{children}</div>
    </div>
  );
}

export function Panel({ title, note, children, className = '' }) {
  return <div className={`mdr-card hre-panel ${className}`}><div className="hre-panel-h"><h3>{title}</h3>{note && <span>{note}</span>}</div>{children}</div>;
}
const Empty = () => <div className="hre-empty">No data for the selected filters.</div>;

export function TypeBar({ rows, title = 'Expenses by Type (Top 8)' }) {
  const data = groupBy(rows, 'expense_type', 8);
  return (
    <Panel title={title}>
      {data.length ? <ResponsiveContainer width="100%" height={260}>
        <BarChart data={data} margin={{ left: 8, right: 8, top: 8, bottom: 30 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
          <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={50} tickFormatter={v => (v.length > 12 ? `${v.slice(0, 11)}…` : v)} />
          <YAxis tick={{ fontSize: 10 }} tickFormatter={kAxis} width={70} />
          <Tooltip formatter={(v, n, p) => [`${naira(v)} · ${p.payload.count} record(s)`, 'Amount']} />
          <Bar dataKey="value" radius={[4, 4, 0, 0]}>{data.map((d, i) => <Cell key={d.name} fill={PALETTE[i % PALETTE.length]} />)}</Bar>
        </BarChart>
      </ResponsiveContainer> : <Empty />}
    </Panel>
  );
}

export function StatusPie({ rows, title = 'Status Distribution' }) {
  const data = statusCounts(rows);
  return (
    <Panel title={title}>
      {data.length ? <div className="md-pie hre-pie">
        <ResponsiveContainer width="100%" height={240}>
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" innerRadius={55} outerRadius={95} paddingAngle={2} stroke="#fff">
              {data.map(x => <Cell key={x.name} fill={x.color} />)}
            </Pie>
            <Tooltip formatter={v => `${v} record(s)`} />
          </PieChart>
        </ResponsiveContainer>
        <ul className="md-legend">{data.map(x => <li key={x.name}><i style={{ background: x.color }} /><span>{x.name}</span><b>{x.value}</b></li>)}</ul>
      </div> : <Empty />}
    </Panel>
  );
}

export function HBar({ rows, by, title, top = 8 }) {
  const data = groupBy(rows, by, top);
  return (
    <Panel title={title}>
      {data.length ? <ResponsiveContainer width="100%" height={Math.max(200, data.length * 32 + 30)}>
        <BarChart data={data} layout="vertical" margin={{ left: 8, right: 16, top: 4 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
          <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={mAxis} />
          <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={120} tickFormatter={v => (v.length > 16 ? `${v.slice(0, 15)}…` : v)} />
          <Tooltip formatter={(v, n, p) => [`${naira(v)} · ${p.payload.count} record(s)`, 'Amount']} />
          <Bar dataKey="value" radius={[0, 4, 4, 0]}>{data.map((d, i) => <Cell key={d.name} fill={PALETTE[i % PALETTE.length]} />)}</Bar>
        </BarChart>
      </ResponsiveContainer> : <Empty />}
    </Panel>
  );
}

export function TrendBar({ rows, year, title = 'Monthly Expense Trend' }) {
  const data = monthlyTrend(rows, year);
  return (
    <Panel title={title} note={year ? String(year) : 'Last 12 months'}>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={data} margin={{ left: 8, right: 8, top: 8 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
          <XAxis dataKey="name" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} tickFormatter={mAxis} width={60} />
          <Tooltip formatter={v => [naira(v), 'Amount']} />
          <Bar dataKey="value" fill="#2563eb" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </Panel>
  );
}

export function downloadCsv(filename, head, rows) {
  const q = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [head.map(q).join(','), ...rows.map(r => r.map(q).join(','))].join('\r\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' }));
  a.download = filename; a.click(); URL.revokeObjectURL(a.href);
}
export const REPORT_HEAD = ['#', 'ID', 'Reference', 'Employee', 'Dept', 'Branch', 'Project Site', 'Expense Name', 'Type', 'Amount', 'Status', 'Payment', 'Payroll Impact', 'Year', 'Month'];
export const reportCells = (r, i) => [i + 1, r.reference, r.ref_no, r.employee || '—', r.department || '—', r.branch || '—', r.site || '—', r.title, r.expense_type,
  r.amount, r.status, r.payment, r.payroll_impact, r.year || '', r.month_name || ''];

const esc = v => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;');
export function printVoucher(x) {
  const lines = (x.lines || []).map(l => `<tr><td>${l.line_no}</td><td>${esc(l.ledger || '')}</td><td>${esc(l.description || '')}</td>
    <td>${esc(l.employee || l.non_employee || '')}</td><td>${esc(l.department || '')}</td><td>${esc(l.basis || '')}</td><td class="r">${esc(naira(l.amount))}</td></tr>`).join('');
  const kv = (k, v) => `<div><small>${k}</small><b>${esc(v ?? '—') || '—'}</b></div>`;
  const html = `<html><head><meta charset="utf-8"><title>${esc(x.voucher_no)}</title><style>
    body{font-family:Arial,sans-serif;color:#0f172a;margin:28px}h1{font-size:20px;margin:0}h2{font-size:13px;margin:18px 0 6px;text-transform:uppercase;letter-spacing:.06em;color:#475569}
    .top{display:flex;justify-content:space-between;border-bottom:3px solid #0b318b;padding-bottom:10px}.grid{display:grid;grid-template-columns:repeat(4,1fr);gap:8px}
    .grid div{border:1px solid #e2e8f0;border-radius:6px;padding:6px 8px}small{display:block;font-size:10px;color:#64748b;text-transform:uppercase}b{font-size:12px}
    table{width:100%;border-collapse:collapse;font-size:11px}th,td{border:1px solid #cbd5e1;padding:5px}th{background:#0b318b;color:#fff;text-align:left}.r{text-align:right}
    .tot{margin-top:10px;margin-left:auto;width:300px}.tot div{display:flex;justify-content:space-between;padding:3px 0;border-bottom:1px dashed #cbd5e1;font-size:12px}
    .sig{display:grid;grid-template-columns:repeat(3,1fr);gap:24px;margin-top:48px}.sig div{border-top:1px solid #0f172a;padding-top:4px;font-size:11px;text-align:center}</style></head><body>
    <div class="top"><div><h1>HR Expense Voucher</h1><div>${esc(x.title)}</div></div><div style="text-align:right"><b>${esc(x.voucher_no)}</b><br>${esc(x.reference)} · ${esc(x.ref_no)}<br>Status: ${esc(x.status)}</div></div>
    <h2>Basic Information</h2><div class="grid">${kv('Expense Type', x.expense_type)}${kv('Date', x.date)}${kv('Maturity Date', x.maturity_date)}${kv('Currency', x.currency)}
    ${kv('JV Reference', x.jv_reference)}${kv('Folio', x.folio)}${kv('Year / Month', `${x.year || ''} ${x.month_name || ''}`)}${kv('Payroll Impact', x.payroll_impact)}</div>
    <h2>Allocation</h2><div class="grid">${kv('Mode', x.allocation_mode)}${kv('Employee', x.employee ? `${x.employee} (${x.employee_no})` : '—')}${kv('Department', x.department)}${kv('Branch', x.branch)}
    ${kv('Project Site', x.site)}${kv('State', x.state)}${kv('Cost Center', x.cost_centre)}${kv('Budget Code', x.budget_code)}</div>
    ${lines ? `<h2>Line Items</h2><table><thead><tr><th>#</th><th>Ledger</th><th>Description</th><th>Employee / Payee</th><th>Department</th><th>Basis</th><th class="r">Amount</th></tr></thead><tbody>${lines}</tbody></table>` : ''}
    <div class="tot"><div><span>Amount</span><b>${naira(x.amount)}</b></div><div><span>VAT${x.vat_applicable ? ` (${x.vat_rate}%)` : ''}</span><b>${naira(x.vat)}</b></div>
    <div><span>WHT${x.wht_applicable ? ` (${x.wht_rate}%)` : ''}</span><b>${naira(x.wht)}</b></div><div><span>Net Total</span><b>${naira(x.net)}</b></div></div>
    ${x.description ? `<h2>Description</h2><p style="font-size:12px">${esc(x.description)}</p>` : ''}
    <div class="sig"><div>Prepared by: ${esc(x.created_by || '')}</div><div>Approved by: ${esc(x.approved_by || '')}</div><div>Received by</div></div>
    <script>window.onload=()=>{window.print()}</script></body></html>`;
  const w = window.open('', '_blank', 'width=900,height=1000');
  if (!w) return false;
  w.document.write(html); w.document.close();
  return true;
}

const ACT = {
  submit: ['Submit for Approval', Send, 'mds-run'], approve: ['Approve', CircleCheckBig, 'mds-run'], reject: ['Reject', X, 'mdr-btn ael-no-btn'],
  return: ['Return for Correction', Undo2, 'mdr-btn'], post: ['Post to Finance', Landmark, 'mdr-btn'], pay: ['Mark Paid', Wallet, 'mdr-btn'],
  cancel: ['Cancel Expense', Ban, 'mdr-btn'], archive: ['Archive', Archive, 'mdr-btn'], restore: ['Restore', RotateCcw, 'mdr-btn'], reopen: ['Reopen as Draft', RotateCcw, 'mdr-btn'],
};
const NEEDS_NOTE = { reject: 'Rejection reason', return: 'What needs correcting' };
const LATER = ['post', 'pay'];

export function DetailModal({ id, notify, onClose, onChanged }) {
  const navigate = useNavigate();
  const [x, setX] = useState(null);
  const [note, setNote] = useState(null);   // {action, text}
  const [busy, setBusy] = useState(false);
  const load = () => hr.hreGet(id).then(setX).catch(e => { notify(e.message, 'bad'); onClose(); });
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!x) return <Modal title="HR Expense Details" onClose={onClose}><Loader title="Loading…" /></Modal>;
  const run = async (action, text = '') => {
    if (NEEDS_NOTE[action] && !text.trim()) { setNote({ action, text: '', err: `${NEEDS_NOTE[action]} is required` }); return; }
    setBusy(true);
    try {
      const r = await hr.hreAction(x.id, { action, note: text });
      notify(r.message); setNote(null); await load(); onChanged?.();
    } catch (e) { notify(e.message, 'bad'); } finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try { const r = await hr.hreDelete(x.id); notify(r.message); onChanged?.(); onClose(); } catch (e) { notify(e.message, 'bad'); } finally { setBusy(false); }
  };
  const acts = x.actions.filter(a => ACT[a]);
  const kv = [['Expense Type', x.expense_type], ['Employee', x.employee ? `${x.employee} · ${x.employee_no}` : '— Dept Level'], ['Department', x.department],
    ['Branch', x.branch], ['Project Site', x.site], ['Project State', x.state], ['Cost Center', x.cost_centre], ['Budget Code', x.budget_code],
    ['Allocation Mode', x.allocation_mode], ['Entry Mode', `${x.entry_mode}${x.employee_count ? ` · ${x.employee_count} employee(s)` : ''}`],
    ['Date', x.date], ['Maturity Date', x.maturity_date], ['Year', x.year], ['Month', x.month_name], ['JV Reference', x.jv_reference], ['Folio', x.folio],
    ['Payment Status', x.payment], ['Payroll Impact', x.payroll_impact], ['Posting', x.posting], ['Created By', x.created_by],
    ['Approved By', x.approved_by ? `${x.approved_by}${x.decided_at ? ` · ${x.decided_at.slice(0, 10)}` : ''}` : null],
    ['With', x.current_step]];
  return (
    <Modal wide title="HR Expense Details" onClose={onClose}>
      <div className="hre-detail">
        <div className="hre-detail-head">
          <span className="hre-avatar">{(x.expense_type || '?')[0]}</span>
          <div><h3>{x.title}</h3><small>{x.reference} · {x.voucher_no} · {x.ref_no}</small></div>
          <Status s={x.status} />
        </div>
        <div className="hre-progress"><i style={{ width: `${x.progress}%` }} /><span>{x.progress}%</span></div>
        {x.decision_note && <div className={`hre-note ${x.status === 'Rejected' ? 'bad' : ''}`}><b>{x.status === 'Rejected' ? 'Rejection reason' : 'To correct'}:</b> {x.decision_note}</div>}
        {x.requires_attachment && <div className="hre-note">This expense type normally needs supporting documents — keep them with the voucher.</div>}
        <div className="hre-money">
          <div><small>Amount</small><b>{naira(x.amount)}</b></div>
          <div><small>VAT{x.vat_applicable ? ` (${x.vat_rate}%)` : ''}</small><b>{naira(x.vat)}</b></div>
          <div><small>WHT{x.wht_applicable ? ` (${x.wht_rate}%)` : ''}</small><b>{naira(x.wht)}</b></div>
          <div className="net"><small>Net Total</small><b>{naira(x.net)}</b></div>
        </div>
        <div className="ael-kv">{kv.map(([k, v]) => <div key={k}><small>{k}</small><span>{v || '—'}</span></div>)}</div>
        {x.route.length > 0 && <>
          <h4 className="wh-sub">Approval Route</h4>
          <div className="hre-route">{x.route.map(s => <span key={s.level} className={`mdr-chip t-${{ approved: 'green', current: 'yellow', returned: 'orange', rejected: 'red' }[s.state] || 'gray'}`}>
            {s.level}. {s.role}{s.state === 'current' ? ' — now' : s.state === 'approved' ? ' ✓' : ''}</span>)}</div>
        </>}
        {x.payroll?.kind && <>
          <h4 className="wh-sub">Payroll</h4>
          <div className="hre-payroll">
            <div className="hre-payroll-h"><Chip tone={x.payroll.tone}>{x.payroll.label}</Chip><span>{x.payroll_impact} · {x.payroll.kind_label}</span></div>
            {x.payroll.shares.length > 0 ? <table className="mdr-table hre-lines-ro">
              <thead><tr><th>Employee</th><th className="r">Amount in Payroll</th></tr></thead>
              <tbody>{x.payroll.shares.map(sh => <tr key={sh.employee_id}><td>{sh.employee} <small className="muted">{sh.employee_no}</small></td>
                <td className="r mono">{x.payroll.kind === 'deduction' ? '−' : ''}{naira(sh.amount)}</td></tr>)}</tbody>
            </table> : <div className="hre-note bad">No employee on this expense, so payroll cannot apply it. Edit it to add the employee, or set Payroll Impact to No Payroll Impact.</div>}
            <small className="muted">Recorded for payroll: the payroll module applies approved expenses to the employees above in the expense month's run (or the next run if approved later).</small>
          </div>
        </>}
        {x.lines.length > 0 && <>
          <h4 className="wh-sub">Line Items ({x.lines.length})</h4>
          <div className="mdr-scroll"><table className="mdr-table hre-lines-ro">
            <thead><tr><th>#</th><th>Ledger</th><th>Description</th><th>Employee / Payee</th><th>Department</th><th>Site</th><th>Basis</th><th className="r">Amount</th></tr></thead>
            <tbody>{x.lines.map(l => <tr key={l.id}><td>{l.line_no}</td><td>{l.ledger || '—'}</td><td>{l.description || '—'}</td>
              <td>{l.employee ? <>{l.employee}<small className="muted"> {l.employee_no}</small></> : l.non_employee || '—'}</td><td>{l.department || '—'}</td><td>{l.site || '—'}</td>
              <td className="small">{l.basis}</td><td className="r mono">{naira(l.amount)}</td></tr>)}</tbody>
            <tfoot><tr><td colSpan={7} className="r"><b>Total</b></td><td className="r mono"><b>{naira(x.lines.reduce((t, l) => t + l.amount, 0))}</b></td></tr></tfoot>
          </table></div>
        </>}
        {(x.description || x.remarks) && <div className="ael-kv">{x.description && <div className="wide"><small>Description</small><span>{x.description}</span></div>}
          {x.remarks && <div className="wide"><small>Remarks / Internal Notes</small><span>{x.remarks}</span></div>}</div>}
        {x.history.length > 0 && <>
          <h4 className="wh-sub">History</h4>
          <ul className="hre-history">{x.history.map((h, i) => <li key={i}><b>{h.action}</b><span>{h.by} · {h.at}</span>{h.note && <small>{h.note}</small>}</li>)}</ul>
        </>}
        {note && <div className="hre-note-form">
          <label className="ael-field">{NEEDS_NOTE[note.action]} *<textarea rows={3} value={note.text} onChange={e => setNote({ ...note, text: e.target.value, err: '' })} /></label>
          {note.err && <div className="ael-err">{note.err}</div>}
          <div className="hre-note-btns"><button className="mdr-btn" onClick={() => setNote(null)}>Cancel</button>
            <button className="mds-run ael-danger" disabled={busy} onClick={() => run(note.action, note.text)}>{ACT[note.action][0]}</button></div>
        </div>}
      </div>
      <div className="mdh-form-foot hre-detail-foot">
        <button className="mdr-btn" onClick={() => printVoucher(x) || notify('Allow pop-ups to print the voucher', 'bad')}><Printer size={13} /> Print Voucher</button>
        {x.editable && <button className="mdr-btn" onClick={() => navigate(go('hre-add', { id: x.id }))}><Pencil size={13} /> Edit</button>}
        {x.actions.includes('delete') && <button className="mdr-btn ael-no-btn" disabled={busy} onClick={remove}><Trash2 size={13} /> Delete Draft</button>}
        <span className="spacer" />
        {x.next_step && <button className="mdr-btn hre-advance" disabled={busy} onClick={() => run(x.next_step.action)}><ArrowRight size={13} /> {x.next_step.label}</button>}
        {acts.map(a => {
          const [label, Ico, cls] = ACT[a];
          if (LATER.includes(a)) return <button key={a} className={cls} disabled title={NOT_YET}><Ico size={13} /> {label}</button>;
          return <button key={a} className={cls} disabled={busy} onClick={() => (NEEDS_NOTE[a] ? setNote({ action: a, text: '' }) : run(a))}><Ico size={13} /> {label}</button>;
        })}
      </div>
    </Modal>
  );
}

export function MessageModal({ meta, ids = [], notify, onClose }) {
  const [f, setF] = useState({ recipient: 'Employee', category: 'HR Expense', message: '' });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!f.message.trim()) { setErr('Message is required'); return; }
    setBusy(true);
    try { const r = await hr.hreMessage({ ...f, ids }); notify(r.message); onClose(); } catch (e) { setErr(e.message); notify(e.message, 'bad'); } finally { setBusy(false); }
  };
  return (
    <Modal title="Send Message" onClose={onClose}>
      <div className="mdr-form wh-form">
        <p className="muted small">{ids.length > 0 ? `About ${ids.length} selected expense(s).` : 'Select expenses in the register first to message their employees, managers or department heads.'}
          {' '}The message goes to their notifications.</p>
        <div className="lvm-grid two">
          <label><span>Recipient</span><Select value={f.recipient} onChange={e => setF({ ...f, recipient: e.target.value })}>{meta.recipients.map(r => <option key={r} value={r}>{r}</option>)}</Select></label>
          <label><span>Category</span><Select value={f.category} onChange={e => setF({ ...f, category: e.target.value })}>{meta.message_categories.map(r => <option key={r} value={r}>{r}</option>)}</Select></label>
        </div>
        <label className="wh-block"><span>Message <b className="wh-req">*</b></span><textarea rows={4} value={f.message} onChange={e => { setF({ ...f, message: e.target.value }); setErr(''); }} />
          {err && <small className="ael-err">{err}</small>}</label>
        <div className="mdh-form-foot wh-form-foot"><button className="mdr-btn" onClick={onClose}>Close</button>
          <button className="mds-run" disabled={busy} onClick={send}><Send size={14} /> {busy ? 'Sending…' : 'Send'}</button></div>
      </div>
    </Modal>
  );
}

export function ConfirmModal({ title, children, okLabel, danger, onOk, onClose }) {
  const [busy, setBusy] = useState(false);
  return (
    <Modal title={title} onClose={onClose}>
      <div className="hre-confirm">{children}</div>
      <div className="mdh-form-foot"><button className="mdr-btn" onClick={onClose}>Cancel</button>
        <button className={`mds-run ${danger ? 'ael-danger' : ''}`} disabled={busy} onClick={async () => { setBusy(true); try { await onOk(); } finally { setBusy(false); } }}>{okLabel}</button></div>
    </Modal>
  );
}
